import { IS_AWS_ENV } from "constants/app.constants";

// Well-header values -> rig/frac values, for fields whose two vocabularies
// differ only in spelling. Pure and synchronous, unlike the operator variants,
// which need a server round trip (useFocusTranslation).

const US_STATE_NAME_BY_CODE: Readonly<Record<string, string>> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming"
};

const US_STATE_CODE_BY_NAME: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(US_STATE_NAME_BY_CODE).map(([code, name]) => [name, code])
);

// AWS well headers carry "TX"; rig/frac carries "Texas". A value that is not a
// known code (already a full name, or a Canadian province) passes through.
export function wellStateToRigFracStates(value: string): string[] {
  return [US_STATE_NAME_BY_CODE[value.toUpperCase()] ?? value];
}

const RIG_FRAC_COUNTRY_BY_CODE: Readonly<Record<string, string>> = {
  US: "United States",
  CA: "Canada"
};

// AWS well headers carry "US"; rig/frac carries "United States". Anything else
// passes through.
export function wellCountryToRigFracCountries(value: string): string[] {
  return [RIG_FRAC_COUNTRY_BY_CODE[value.toUpperCase()] ?? value];
}

const WELL_COUNTY_PATTERN = /^(.+), ([A-Z]{2})$/;

// AWS well headers carry "Blaine, OK". Rig/frac countyUnique suffixes the state
// only when the county name exists in more than one state ("Caddo (OK)"), and
// leaves a name that exists in one state bare ("Canadian"), so both spellings
// are candidates. The bare one can't match another state's county: a colliding
// name is always suffixed.
export function wellCountyToRigFracCounties(value: string): string[] {
  const match = WELL_COUNTY_PATTERN.exec(value);
  if (!match) return [value];
  const [, name, state] = match;
  return [`${name} (${state})`, name];
}

const RIG_FRAC_COUNTY_PATTERN = /^(.+) \(([A-Z]{2})\)$/;

// Display only: shows countyUnique in the AWS wells' "Name, ST" format. The
// value sent to every filter stays countyUnique. A bare name ("Andrews") takes
// its state from `stateProvince`, and stays bare when that is unknown. Azure
// keeps countyUnique as-is.
export function formatRigFracCountyLabel(
  countyUnique: string,
  stateProvince?: Maybe<string>
): string {
  if (!IS_AWS_ENV) return countyUnique;
  const match = RIG_FRAC_COUNTY_PATTERN.exec(countyUnique);
  if (match) return `${match[1]}, ${match[2]}`;
  const code = stateProvince ? US_STATE_CODE_BY_NAME[stateProvince] : undefined;
  return code ? `${countyUnique}, ${code}` : countyUnique;
}

// Display only: AWS wells carry "TX" where rig/frac carries "Texas". Azure, and
// a name with no US code, keep the stored value.
export function formatRigFracStateLabel(stateProvince: string): string {
  if (!IS_AWS_ENV) return stateProvince;
  return US_STATE_CODE_BY_NAME[stateProvince] ?? stateProvince;
}

// AWS names these well fields "Play/Sub Basin" and "Field/County"; the rig/frac
// controls use the same names there so a focus and the rig/frac field it
// filters read as one. Azure keeps each control's own label.
export function rigFracSubBasinFieldName(azureName: string): string {
  return IS_AWS_ENV ? "Play/Sub Basin" : azureName;
}

export function rigFracCountyFieldName(azureName: string): string {
  return IS_AWS_ENV ? "Field/County" : azureName;
}
